import { ActionIcon, Box, Checkbox, Group, Loader, Stack, Tooltip, useMantineTheme } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronRight } from "@tabler/icons-react";
import { Suspense } from "react";
import { useColorMode } from "shared/hooks/useColorMode";
import { TopicLikeFragment } from "src/gql/graphql";

export type NestedTopicTree = TopicLikeFragment & {
    children?: TopicLikeFragment[] & {
        children?: TopicLikeFragment[] & { children?: TopicLikeFragment[] & { children?: TopicLikeFragment[] } };
    };
};

export const TopicNode = ({
    topic,
    isChecked,
    initialExpandedNodes,
    shouldRenderCheckbox,
    checkboxOnChange,
    isDisabled,
    disabledTooltipLabel,
    isIndeterminate,
    topicFilter,
}: {
    topic: NestedTopicTree;
    isChecked: (topic: TopicLikeFragment) => boolean;
    initialExpandedNodes: string[];
    shouldRenderCheckbox: (topic: TopicLikeFragment) => boolean;
    checkboxOnChange: (topic: TopicLikeFragment, checked: boolean) => void;
    isDisabled?: (topic: TopicLikeFragment) => boolean;
    disabledTooltipLabel?: string | ((topic: TopicLikeFragment) => string | undefined);
    isIndeterminate?: (topic: TopicLikeFragment) => boolean;
    topicFilter?: (topic: TopicLikeFragment) => boolean;
}) => {
    const theme = useMantineTheme();
    const { isDark } = useColorMode();
    const [opened, { toggle }] = useDisclosure(initialExpandedNodes.includes(topic.id));

    const shouldShowExpandButton = topic.isCategory && (topic.children?.filter(topicFilter ?? (() => true)).length ?? 0) > 0;
    const tooltipLabel = typeof disabledTooltipLabel === "function" ? disabledTooltipLabel(topic) : disabledTooltipLabel;

    return (
        <Box pos="relative">
            <Stack gap={0} style={{ display: "inline-flex" }} w="min-content">
                <Group gap={0} w="min-content" p="sm" pl={shouldShowExpandButton ? 0 : 28} wrap="nowrap" style={{ whiteSpace: "nowrap" }}>
                    <ActionIcon
                        display={shouldShowExpandButton ? undefined : "none"}
                        variant="transparent"
                        color="text"
                        onClick={toggle}
                        aria-label="Show or hide subtopics"
                        styles={{ icon: { justifyContent: "flex-start" } }}
                    >
                        <IconChevronRight
                            size={16}
                            style={{ transition: "transform 0.5s", transform: opened ? "rotate(90deg)" : "rotate(0deg)" }}
                        />
                    </ActionIcon>
                    {shouldRenderCheckbox(topic) ? (
                        <Tooltip disabled={!isDisabled?.(topic)} label={tooltipLabel}>
                            <Checkbox
                                indeterminate={isIndeterminate?.(topic)}
                                disabled={isDisabled?.(topic)}
                                checked={isChecked(topic)}
                                onChange={(e) => checkboxOnChange(topic, e.target.checked)}
                                pr="sm"
                                label={topic.name}
                            />
                        </Tooltip>
                    ) : (
                        topic.name
                    )}
                </Group>
                <Suspense fallback={<Loader />}>
                    {opened && (
                        <Stack
                            gap={0}
                            w="fit-content"
                            style={{
                                borderLeft: `var(--mantine-spacing-md) solid ${isDark ? theme.colors.dark[5] : theme.colors.gray[1]}`,
                                overflow: "auto",
                            }}
                        >
                            <Box>
                                {topic.children
                                    ?.filter(topicFilter ?? (() => true))
                                    .map((c) => (
                                        <TopicNode
                                            key={c.id}
                                            topic={c}
                                            isChecked={isChecked}
                                            initialExpandedNodes={initialExpandedNodes}
                                            shouldRenderCheckbox={shouldRenderCheckbox}
                                            checkboxOnChange={checkboxOnChange}
                                            isDisabled={isDisabled}
                                            disabledTooltipLabel={disabledTooltipLabel}
                                            isIndeterminate={isIndeterminate}
                                            topicFilter={topicFilter}
                                        />
                                    ))}
                            </Box>
                        </Stack>
                    )}
                </Suspense>
            </Stack>
        </Box>
    );
};
